import { Ajax2 } from "@shared/Ajax";
import { Auth } from "@shared/Auth";
import SparkMD5 from 'spark-md5';


export interface S3PresignResult {
    uploadUrl: string; // https://s3.....amazonaws.com/foo.infrakit.info/file-service/2023/08-27/uuid
    contentType: string;
    expires: string;
    document: {
        uuid: string;
        headUuid: string;
        name: string;
        folderPath: string; // "/Alignments/etc/etc"
        uuidPath: string; // "/bff26d31-9b51-11ea-99a3-01ca6403320/bff28355-9b51-11ea-99a3-06es6403320"
        timestamp: string; // "2023-08-27T11:51:30.055Z"
        state: 'PENDING_UPLOAD' | string;
        filesize: number;
        md5: number;
    }

    // calculated by service
    checksumMd5b64: string;
}


const refreshToken = _.debounce(async () => {
    console.warn('Token expired, refreshing');
    await Auth.refreshToken();
}, 500);

export const FileServiceClient = {

    getDownloadUrl: async (objectKey: string, bucketName?: string): Promise<string> => {
        try {

            const token = Auth.getStoredToken();

            if (Auth.isTokenExpired(token)) {
                await refreshToken();
            }

            const resp = await Ajax2({
                url: kuura.getFileServiceAddress() + '/v1/file/async-download',
                method: 'POST',
                useToken: true,
                data: {
                    bucketName: bucketName,
                    objectKey: objectKey
                }
            });

            return resp.downloadUrl;

        } catch (e) {
            console.warn("Encountered an error when fetching download link from File Service");
            return null;
        }

    },

    getS3UploadPath: async (file: File, opts: {
        fileType: string,
        folderUuid: string;
        filename: string;
        headUuid?: string; // for new versions
        description?: string;
        checksum?: string;
        comment?: string;
        contentType?: string;
        direction?: number;
        originalTimestamp?: number;
        altitude?: number;
        reportTemplateId?: number;
        size?: number;
        lat?: number;
        lon?: number;
        properties?: Record<string, any>;
        deflate?: boolean;
    }): Promise<S3PresignResult> => {
        console.log('getS3UploadPath for file:', file, 'options:', opts);

        try {
            opts.checksum = await hashIncremental(file, false);
            const checksumMd5b64 = btoa(await hashIncremental(file, true));
            opts.size = file.size;
            if (!opts.contentType) {
                opts.contentType = file.type?.length ? file.type : 'application/octet-stream';
            }

            return Ajax2({
                url: `${kuura.getContextPath()}/v1/${opts.fileType}/async-upload`,
                method: 'POST',
                contentType: 'json',
                data: opts
            }).then(resp => {
                resp.checksumMd5b64 = checksumMd5b64;
                resp.contentType = opts.contentType;
                return resp;
            }).catch(e => {
                console.log('getS3UploadPath /async-upload error:', e);
                throw e; // uploader also needs to react to error
            })
        } catch (e) {
            console.log('e:', e);
        }
    }
}

// needs to be done incrementally because chrome has 2gb limit for arraybuffers and we support >2gb files
function hashIncremental(file: File, raw: boolean): Promise<string> {
    return new Promise((done, fail) => {
        let blobSlice = File.prototype.slice;
        let chunkSize = 2097152; // Read in chunks of 2MB
        let chunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        let spark = new SparkMD5.ArrayBuffer();
        let fileReader = new FileReader();

        fileReader.onload = function (e) {
            spark.append(e.target.result);
            currentChunk++;

            if (currentChunk < chunks) {
                loadNext();
            } else {
                done(spark.end(raw));
            }
        };

        fileReader.onerror = () => fail('hashing failed');

        function loadNext() {
            let start = currentChunk * chunkSize;
            let end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;

            fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
        }

        loadNext();
    })
}
